import { provideEditingConfig } from "scrivito";
import { SchemaWidget } from "./SchemaWidgetClass";

const postalAddressAttributes = [
  "streetAddress",
  "addressLocality",
  "addressRegion",
  "postOfficeBoxNumber",
  "postalCode",
  "addressCountry",
  "locationUrl",
];

const eventAttributes = [
  "startDate",
  "endDate",
  "organizerName",
  "url",
  "eventType",
  "eventSubtype",
];

const schemaProperties: Record<string, string[]> = {
  CollegeOrUniversity: [
    "url",
    "logo",
    "email",
    "telephone",
    "locationName",
    "locationDescription",
    ...postalAddressAttributes,
  ],
  EducationalOccupationalProgram: [
    "url",
    "educationalCredentialAwarded",
    "numberOfCredits",
    "timeToComplete",
    "applicationDeadline",
    "programPrerequisites",
  ],
  ContactPoint: [
    "areaServed",
    "availableLanguage",
    "contactType",
    "email",
    "hoursAvailable",
    "telephone",
  ],
  Event: [...eventAttributes],
  EventOffline: [
    ...eventAttributes,
    "locationName",
    "locationDescription",
    ...postalAddressAttributes,
  ],
  EventOnline: [...eventAttributes, "locationUrl"],
  EducationalOrganization: [
    "url",
    "logo",
    "email",
    "telephone",
    "locationName",
    "locationDescription",
    ...postalAddressAttributes,
  ],
  JobPosting: [
    "jobTitle",
    "datePosted",
    "employmentType",
    "employmentTypeOther",
    "organizationName",
    "url",
    "baseSalary",
    "validThrough",
    "locationName",
    "locationDescription",
    ...postalAddressAttributes,
  ],
  Person: [
    "honorificPrefix",
    "givenName",
    "familyName",
    "email",
    "telephone",
    "jobTitle",
    "locationName",
    "locationDescription",
    ...postalAddressAttributes,
  ],
  PostalAddress: [...postalAddressAttributes],
  Product: ["brandName", "sellerName", "url"],
  Book: [
    "authorName",
    "organizationName",
    "accessMode",
    "bookFormat",
    "isbn",
    "datePublished",
    "availableLanguage",
    "url",
  ],
  CreativeWork: [
    "authorName",
    "organizationName",
    "report",
    "techArticle",
    "url",
  ],
  Review: [
    "reviewBody",
    "itemName",
    "itemDescription",
    "ratingValue",
    "authorName",
  ],
};

provideEditingConfig(SchemaWidget, {
  title: "Schema",
  attributes: {
    accessMode: {
      description: "The mode of access for the resource, e.g. PDF or EPUB",
    },
    addressCountry: {
      description: "ISO country code, e.g. DE for Germany or AT for Austria",
    },
    areaServed: {
      description: "The geographic area served, e.g. Germany or Baden-Württemberg",
    },
    baseSalary: {
      description: "Monthly salary in euros (number only, no currency symbol)",
    },
    bookFormat: {
      description: "The format of the book, e.g. Hardcover, Paperback or EBook",
    },
    contactType: {
      description: "The type of contact, e.g. admissions, student support or technical support",
    },
    educationalCredentialAwarded: {
      description: "The credential awarded, e.g. Bachelor of Science or Master of Engineering",
    },
    employmentTypeOther: {
      description: "Free-text employment type — only fill in when OTHER is selected above",
    },
    eventSubtype: {
      description: "Use EducationEvent for lectures, open days or other university events",
    },
    eventType: {
      description: "Place for in-person events, VirtualLocation for online events",
    },
    hoursAvailable: {
      description: "Availability in plain text, e.g. Mon–Fri 08:00–17:00",
    },
    addressLocality: {
      description: "City or town, e.g. Heilbronn",
    },
    addressRegion: {
      description: "State or federal state, e.g. Baden-Württemberg",
    },
    email: {
      description: "Contact e-mail address",
    },
    familyName: {
      description: "Last name / surname of the person",
    },
    givenName: {
      description: "First name of the person",
    },
    honorificPrefix: {
      description: "Academic title preceding the name, e.g. Prof. Dr. or Dr.",
    },
    jobTitle: {
      description: "Role or position of the person, e.g. Professor für Informatik",
    },
    postOfficeBoxNumber: {
      description: "PO box number if applicable",
    },
    postalCode: {
      description: "Postal code / ZIP code, e.g. 74074",
    },
    streetAddress: {
      description: "Street name and house number, e.g. Max-Planck-Straße 39",
    },
    telephone: {
      description: "Phone number in international format, e.g. +49 7131 504-0",
    },
    locationUrl: {
      description: "URL of the virtual event, e.g. a Zoom or Teams link",
    },
    programPrerequisites: {
      description: "Admission requirements for the programme, e.g. Allgemeine Hochschulreife",
    },
    ratingValue: {
      description: "Rating between 1.0 and 5.0 or as a percentage, e.g. 4.5 or 75%",
    },
    report: {
      description: "URL to a related report or study",
    },
    techArticle: {
      description: "URL to a related technical or academic article",
    },
    timeToComplete: {
      description: "Programme duration as ISO 8601 duration, e.g. P7S for 7 semesters or P3Y for 3 years",
    },
    validThrough: {
      description: "The date until which the job posting is valid",
    },
  },
  properties: (widget) => {
    const baseAttributes = ["type", "name", "description"];
    let type = widget.get("type") || "";

    if (type === "Event" && widget.get("eventType") === "VirtualLocation") {
      type = "EventOnline";
    } else if (type === "Event" && widget.get("eventType") === "Place") {
      type = "EventOffline";
    }

    let properties = schemaProperties[type];

    if (type === "JobPosting" && widget.get("employmentType") !== "OTHER") {
      properties = properties.filter((item) => item !== "employmentTypeOther");
    }

    return type in schemaProperties
      ? [...baseAttributes, ...properties]
      : ["type"];
  },
});
